import { Alert } from '@creditornot/cb-alert';
import { IconButton } from '@creditornot/cb-button';
import { Minus, Plus } from '@creditornot/cb-icons';
import { cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import * as R from 'ramda';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import TimePairInput from 'common/components/weekly-schedules/TimePairInput';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';

import WeekdaySelect from './WeekdaySelect';
import { DEFAULT_TIME_PAIR, WEEKDAYS, daysOfWeek, daysOfWeekShort } from './constants';
import { TimePair, TimePairsByWeekday } from './types';
import { checkForOverlappingTimePairs, checkIf24h } from './utils';

export type Props = {
  value: TimePairsByWeekday;
  onChange: (value: TimePairsByWeekday) => void;
};

const Root = styled.div``;

const WeekdayRow = styled.div`
  display: flex;
  margin-block-end: 8px;
`;

const WeekdayLabel = styled(T.Body3)`
  height: 36px;
  display: flex;
  align-items: center;
`;

const TimePairsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  justify-content: space-between;
`;

const TimePairBlock = styled.div`
  display: flex;
  margin-inline-start: auto;
  align-items: center;
`;

const RemoveTimeButton = styled(IconButton).attrs({
  size: 'tiny',
  variant: 'red',
  background: 'static',
})`
  margin-inline-start: 8px;
`;

const AddTimeButton = styled(IconButton).attrs({
  size: 'tiny',
  variant: 'blue',
  background: 'static',
})`
  margin-inline-start: 8px;
`;

const InfoMessage = styled(Alert).attrs({
  variant: 'info',
  size: 'small',
})`
  margin-block-end: 24px;
`;

const Closed = styled(T.Body3)`
  height: 36px;
  display: flex;
  align-items: center;
  font-weight: 400;
  margin-inline-start: auto;
`;

const Tag = styled.div`
  margin-inline-end: 8px;
  padding-inline: 8px;
  padding-block: 4px;
  border-radius: 8px;
  ${typographyCss.Tiny}
  background: ${cssThemedValue('borderPrimary')};
  color: ${cssThemedValue('textPrimary')};
`;

const WeeklyScheduleInput = ({ value: weeklySchedule, onChange }: Props) => {
  const isMobile = useIsMobile();
  const [selectedDays, setWeekDays] = useState(WEEKDAYS.filter(day => weeklySchedule[day].length > 0));

  const hasIncompleteDays = selectedDays.length === 0;
  const handleDaySelect = (dayOfWeek: Weekday) => {
    if (selectedDays.includes(dayOfWeek)) {
      setWeekDays(selectedDays.filter(day => day !== dayOfWeek));
      onChange({
        ...weeklySchedule,
        [dayOfWeek]: [],
      });
    } else {
      setWeekDays([...selectedDays, dayOfWeek]);
      onChange({
        ...weeklySchedule,
        [dayOfWeek]: [DEFAULT_TIME_PAIR],
      });
    }
  };

  return (
    <Root data-test-id={'WeeklyScheduleInput'}>
      <WeekdaySelect value={selectedDays} onChange={handleDaySelect} />

      {WEEKDAYS.map(dayOfWeek => {
        const hasOverlappingTimePairs = checkForOverlappingTimePairs(weeklySchedule[dayOfWeek]);
        const is24h = checkIf24h(weeklySchedule[dayOfWeek]);
        const isDaySelected = selectedDays.includes(dayOfWeek);

        const renderDayOfWeek = isMobile ? daysOfWeekShort[dayOfWeek] : daysOfWeek[dayOfWeek];

        return (
          <>
            <WeekdayRow key={dayOfWeek} data-test-id={`${dayOfWeek}-row`}>
              <WeekdayLabel>
                <FormattedMessage id={renderDayOfWeek.id} defaultMessage={renderDayOfWeek.default} />
              </WeekdayLabel>

              {isDaySelected ? (
                <TimePairsContainer>
                  {weeklySchedule[dayOfWeek].map((timePair: TimePair, timePairIndex: number) => {
                    const isFirstTimePair = timePairIndex === 0;
                    return (
                      <TimePairBlock key={timePairIndex}>
                        {isFirstTimePair && is24h && <Tag>24h</Tag>}
                        <TimePairInput
                          onChange={newTimePair => {
                            onChange({
                              ...weeklySchedule,
                              [dayOfWeek]: R.update(timePairIndex, newTimePair, weeklySchedule[dayOfWeek]),
                            });
                          }}
                          value={timePair}
                        />

                        {isFirstTimePair ? (
                          <AddTimeButton
                            disabled={weeklySchedule[dayOfWeek].length === 3}
                            onClick={() => {
                              onChange({
                                ...weeklySchedule,
                                [dayOfWeek]: [...weeklySchedule[dayOfWeek], DEFAULT_TIME_PAIR],
                              });
                            }}
                          >
                            <Plus />
                          </AddTimeButton>
                        ) : (
                          <RemoveTimeButton
                            disabled={weeklySchedule[dayOfWeek].length === 1}
                            onClick={() => {
                              onChange({
                                ...weeklySchedule,
                                [dayOfWeek]:
                                  weeklySchedule[dayOfWeek].length > 1
                                    ? R.remove(timePairIndex, 1, weeklySchedule[dayOfWeek])
                                    : [DEFAULT_TIME_PAIR],
                              });
                            }}
                          >
                            <Minus />
                          </RemoveTimeButton>
                        )}
                      </TimePairBlock>
                    );
                  })}
                </TimePairsContainer>
              ) : (
                <Closed>
                  <FormattedMessage id="opening-hours.closed" defaultMessage="Closed" />
                </Closed>
              )}
            </WeekdayRow>
            {hasOverlappingTimePairs && (
              <InfoMessage>
                <FormattedMessage
                  id="opening-hours.hours-overlap-info"
                  defaultMessage="Your opening hours overlap with each other. Please check them before submitting."
                />
              </InfoMessage>
            )}
          </>
        );
      })}
      {hasIncompleteDays && (
        <InfoMessage>
          <FormattedMessage id="opening-hours.incomplete-entries-info" defaultMessage="Some entries seem incomplete." />
        </InfoMessage>
      )}
    </Root>
  );
};

export default WeeklyScheduleInput;
